<template>
  <div class="kyc-modal">
    <p v-if="isLoading" class="text-subtle">Hämtar KYC...</p>

    <template v-else-if="loadError">
      <p class="error-text">{{ loadError }}</p>
      <div class="actions">
        <AppButton type="button" @click="emit('close')">Stäng</AppButton>
        <AppButton type="button" color="blue" @click="load">Försök igen</AppButton>
      </div>
    </template>

    <template v-else-if="questionStatus">
      <div v-if="isEditable && hasPrefill" class="info-box">
        <p>
          Vissa uppgifter är förifyllda från kundens tidigare KYC eller ansökan. Kontrollera att de fortfarande stämmer
          innan du skickar.
        </p>
      </div>

      <DataSection v-if="user">
        <DataField label="Kund">{{ user.givenName }} {{ user.surname }}</DataField>
        <DataField label="Personnummer">{{ user.personalNumber }}</DataField>
      </DataSection>

      <section class="progress" aria-label="Besvarade frågor">
        <p class="text-subtle">{{ kycAnswersCountText(answeredCount, questionStatus.questions.length) }}</p>
        <ul class="progress-list">
          <li v-for="question in questionStatus.questions" :key="question.key">
            <AppChip :color="question.answered ? 'success' : undefined" :dot="question.answered">
              {{ label(question.key) }}
            </AppChip>
          </li>
        </ul>
      </section>

      <template v-if="!isEditable">
        <div class="info-box">
          <p>
            Den här KYC:n väntar inte på svar ({{ translateRef('kycStatuses', questionStatus.status).toLowerCase() }}).
            Svaren kan inte ändras.
          </p>
        </div>
        <KycAnswersView :answers="cycleAnswers" />
        <div class="actions">
          <AppButton type="button" @click="emit('close')">Stäng</AppButton>
        </div>
      </template>

      <AppValidationGroup v-else v-slot="{ validate }">
        <form class="kyc-form" novalidate @submit.prevent="validate(() => void submit())">
          <p v-if="serverMessages.other.length" class="error-text" role="alert">{{ serverMessages.other.join(' ') }}</p>

          <fieldset class="form-section">
            <legend>Kontaktuppgifter</legend>
            <div class="field-grid">
              <AppInput
                v-model="form.mobilePhone"
                type="tel"
                :label="label('mobilePhone')"
                :disabled="isSubmitting"
                :rules="kycFieldRules.mobilePhone"
                :error-messages="serverMessages.byQuestion.mobilePhone"
              />
              <AppInput
                v-model="form.email"
                type="email"
                :label="label('email')"
                :disabled="isSubmitting"
                :rules="kycFieldRules.email"
                :error-messages="serverMessages.byQuestion.email"
              />
            </div>
          </fieldset>

          <fieldset class="form-section">
            <legend>Medborgarskap och skatterättslig hemvist</legend>
            <div class="country-lists">
              <CountryIdList
                v-model="form.citizenships"
                variant="citizenship"
                :label="label('citizenships')"
                :id-label="label('citizenships.registrationId')"
                add-label="Lägg till medborgarskap"
                :disabled="isSubmitting"
              />
              <p v-if="serverMessages.byQuestion.citizenships" class="error-text">
                {{ serverMessages.byQuestion.citizenships.join(' ') }}
              </p>
              <CountryIdList
                v-model="form.taxDomiciles"
                variant="taxDomicile"
                :label="label('taxDomiciles')"
                :id-label="label('taxDomiciles.tin')"
                add-label="Lägg till skatterättslig hemvist"
                :disabled="isSubmitting"
              />
              <p v-if="serverMessages.byQuestion.taxDomiciles" class="error-text">
                {{ serverMessages.byQuestion.taxDomiciles.join(' ') }}
              </p>
            </div>
          </fieldset>

          <fieldset class="form-section">
            <legend>Anställning och inkomst</legend>
            <div class="field-grid">
              <AppSelect
                :model-value="form.employmentStatus"
                :label="label('employmentDetails.employmentStatus')"
                :options="referenceOptions('kycEmploymentStatuses')"
                placeholder="Välj anställningsform"
                :disabled="isSubmitting"
                :rules="kycFieldRules.employmentStatus"
                :error-messages="serverMessages.byQuestion.employmentDetails"
                @update:model-value="setEmploymentStatus"
              />
              <AppInput
                v-model="form.employmentStartYearMonth"
                :label="label('employmentDetails.employmentStartYearMonth')"
                placeholder="ÅÅÅÅ-MM"
                :disabled="isSubmitting"
                :rules="kycFieldRules.employmentStartYearMonth"
              />
              <AppInput
                v-if="needsEmploymentEnd(form.employmentStatus)"
                v-model="form.employmentEndYearMonth"
                :label="label('employmentDetails.employmentEndYearMonth')"
                placeholder="ÅÅÅÅ-MM"
                :disabled="isSubmitting"
                :rules="kycFieldRules.employmentEndYearMonth"
              />
              <AppInput
                v-if="hasEmployer(form.employmentStatus)"
                v-model="form.employer"
                :label="label('employer')"
                :hint="employerHint"
                :disabled="isSubmitting"
                :rules="kycFieldRules.employer"
                :error-messages="serverMessages.byQuestion.employer"
              />
              <!-- Not type="amount": it drops every non-digit, so "42000,5" would be sent as 420005 instead of
                   being refused by the whole-kronor rule. -->
              <AppInput
                v-model="form.monthlyIncome"
                inputmode="numeric"
                suffix="kr/mån"
                :label="label('monthlyIncome')"
                :hint="prefill.fromApplication.monthlyIncome ? FROM_APPLICATION_NOTICE : undefined"
                :disabled="isSubmitting"
                :rules="kycFieldRules.monthlyIncome"
                :error-messages="serverMessages.byQuestion.monthlyIncome"
              />
            </div>
          </fieldset>

          <fieldset class="form-section">
            <legend>Extra amorteringar</legend>
            <div class="field-grid">
              <AppSelect
                v-model="form.extraAmortizationFrequency"
                :label="label('extraAmortizationFrequency')"
                :options="referenceOptions('kycExtraAmortizationFrequencies')"
                placeholder="Välj antal"
                :disabled="isSubmitting"
                :rules="kycFieldRules.extraAmortizationFrequency"
              />
              <AppSelect
                v-model="form.extraAmortizationVolume"
                :label="label('extraAmortizationVolume')"
                :options="referenceOptions('kycExtraAmortizationVolumes')"
                placeholder="Välj belopp"
                :disabled="isSubmitting"
                :rules="kycFieldRules.extraAmortizationVolume"
              />
            </div>
          </fieldset>

          <fieldset class="form-section">
            <legend>PEP</legend>
            <AppRadioGroup
              v-model="form.pepStatus"
              :label="label('pepStatus')"
              :options="referenceOptions('kycPepStatuses')"
              direction="column"
              :disabled="isSubmitting"
              :rules="kycFieldRules.pepStatus"
            />
          </fieldset>

          <div class="actions">
            <AppButton type="button" :disabled="isSubmitting" @click="emit('close')">Avbryt</AppButton>
            <AppButton type="submit" color="green" :disabled="isSubmitting" :loading="isSubmitting"
              >Skicka in</AppButton
            >
          </div>
        </form>
      </AppValidationGroup>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { AppButton, AppChip, AppInput, AppRadioGroup, AppSelect, AppValidationGroup, CountryIdList } from '@staff/ui'
import { kycAnswersCountText } from '@staff/shared'

import DataField from '@/components/DataField.vue'
import DataSection from '@/components/DataSection.vue'
import { useMetadata } from '@/composables/useMetadata'
import KycAnswersView from './KycAnswersView.vue'
import { hasEmployer, kycFieldRules, needsEmploymentEnd } from './kycForm'
import { useKycAnswersForm } from './useKycAnswersForm'

const props = defineProps<{
  kycId: string
  /** The customer the KYC is for: their KYC list holds the answers the cycle already has. */
  userId: string
  onSubmitted: () => unknown
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const FROM_APPLICATION_NOTICE = 'Förifyllt från ansökan — kontrollera innan du skickar'

const { fieldLabel, getReferenceValues, translateRef } = useMetadata()

const label = (field: string) => fieldLabel('kycAnswers', field)
const referenceOptions = (reference: string) =>
  getReferenceValues(reference).map((option) => ({ text: option.label, value: option.code }))

const {
  isLoading,
  loadError,
  isSubmitting,
  isEditable,
  user,
  questionStatus,
  cycleAnswers,
  prefill,
  form,
  serverMessages,
  load,
  setEmploymentStatus,
  submit,
} = useKycAnswersForm({
  kycId: props.kycId,
  userId: props.userId,
  onSubmitted: () => props.onSubmitted(),
  onDone: () => emit('close'),
})

// Any answer filled in from the customer's earlier KYC or from the application: the hint at the top asks
// for every field to be checked before sending.
const hasPrefill = computed(
  () =>
    prefill.value.suggestionReceivedAt !== null ||
    prefill.value.fromApplication.monthlyIncome ||
    prefill.value.fromApplication.employer,
)
const answeredCount = computed(
  () => questionStatus.value?.questions.filter((question) => question.answered).length ?? 0,
)

const employerHint = computed(() => {
  if (prefill.value.fromApplication.employer) return FROM_APPLICATION_NOTICE
  return form.employmentStatus === 'selfEmployed' ? 'Företagets namn' : undefined
})
</script>

<style scoped lang="scss">
.kyc-modal,
.kyc-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  container-type: inline-size;

  legend {
    margin-bottom: var(--space-12);
    padding: 0;
    font-weight: var(--font-weight-semibold);
  }
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-16);
}

@container (min-width: 32rem) {
  .field-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.country-lists {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
}

.progress {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
}

.progress-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-8);
  margin: 0;
  padding: 0;
  list-style: none;
}

.info-box {
  padding: var(--space-12);
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-md);
  background-color: var(--color-canvas-inset);
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
}
</style>
